import { NavLink } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { FileText, TrendingUp, Calendar, ArrowRight, Plus } from 'lucide-react';
import { estimatesApi } from '@/api/client';
import { formatCurrency } from '@medical-counsellor/shared';

export function DashboardPage() {
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    // Fetch recent estimates
    const { data: estimatesData, isLoading } = useQuery({
        queryKey: ['estimates', 'recent'],
        queryFn: () => estimatesApi.list(undefined, 1, 5),
    });

    const recentEstimates = estimatesData?.data || [];

    // Calculate stats (this would normally come from a dedicated stats endpoint)
    const todayCount = recentEstimates.filter((e: any) => {
        const today = new Date().toDateString();
        return new Date(e.createdAt).toDateString() === today;
    }).length;

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Welcome header */}
            <div>
                <h1 className="text-2xl font-bold text-slate-900">
                    Welcome back, {user.fullName?.split(' ')[0] || 'User'}
                </h1>
                <p className="text-slate-600 mt-1">
                    Here's what's happening with your estimates today.
                </p>
            </div>

            {/* Quick actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <NavLink
                    to="/estimates/new"
                    className="card group hover:shadow-md transition-shadow"
                >
                    <div className="card-body flex items-center gap-4">
                        <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-xl flex items-center justify-center group-hover:bg-primary-600 group-hover:text-white transition-colors">
                            <Plus className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-slate-900">New Estimate</h3>
                            <p className="text-sm text-slate-500">Create a cost estimate</p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-slate-400 ml-auto group-hover:translate-x-1 transition-transform" />
                    </div>
                </NavLink>

                <div className="card">
                    <div className="card-body flex items-center gap-4">
                        <div className="w-12 h-12 bg-accent-100 text-accent-600 rounded-xl flex items-center justify-center">
                            <FileText className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-slate-900">{todayCount}</h3>
                            <p className="text-sm text-slate-500">Estimates today</p>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body flex items-center gap-4">
                        <div className="w-12 h-12 bg-success-50 text-success-600 rounded-xl flex items-center justify-center">
                            <TrendingUp className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-slate-900">{recentEstimates.length}</h3>
                            <p className="text-sm text-slate-500">Recent estimates</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent estimates */}
            <div className="card">
                <div className="card-header flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Recent Estimates</h2>
                    <NavLink
                        to="/estimates"
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                        View all
                    </NavLink>
                </div>

                {isLoading ? (
                    <div className="card-body">
                        <div className="animate-pulse space-y-4">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="h-16 bg-slate-100 rounded-lg" />
                            ))}
                        </div>
                    </div>
                ) : recentEstimates.length === 0 ? (
                    <div className="card-body text-center py-12">
                        <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-slate-900">No estimates yet</h3>
                        <p className="text-slate-500 mt-1">Create your first estimate to get started.</p>
                        <NavLink to="/estimates/new" className="btn btn-primary btn-md mt-4">
                            Create Estimate
                        </NavLink>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Reference</th>
                                    <th>Patient</th>
                                    <th>Procedure</th>
                                    <th>Amount</th>
                                    <th>Date</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentEstimates.map((estimate: any) => (
                                    <tr key={estimate.id}>
                                        <td className="font-medium text-slate-900">
                                            {estimate.referenceNumber}
                                        </td>
                                        <td>{estimate.patientName}</td>
                                        <td>{estimate.procedureConfig?.procedure?.name || '-'}</td>
                                        <td className="font-semibold text-slate-900">
                                            {formatCurrency(estimate.totalAmount)}
                                        </td>
                                        <td className="text-slate-500">
                                            {new Date(estimate.createdAt).toLocaleDateString('en-IN')}
                                        </td>
                                        <td>
                                            <NavLink
                                                to={`/estimates/${estimate.id}`}
                                                className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                                            >
                                                View
                                            </NavLink>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
